/* eslint class-methods-use-this: 0 */
import { DirectoryGroupingConsts, ItemTypeConsts } from 'client/util/Constants';
import { parallelLimit } from 'async';
import Api from 'client/api/v2/Api';
import SystemGroupApi from 'client/api/v2/SystemGroupApi';
import UserGroupApi from 'client/api/v2/UserGroupApi';
import axios from 'axios';
import pluralize from 'pluralize';

const { groupTypes } = DirectoryGroupingConsts;

/**
 * This is a port of the original directoryGroupingUtils.js to ES6 and to use
 * Axios instead of jQuery.ajax(). In addition, it differs from the original
 * implementation in that only a single object is updated in each request.
 * Batch requesting of multilple objects will be done in Vuex actions instead
 * to enable features such as progress indication.
 */
export class DirectoryGroupingApiClass extends Api {
  /**
   * Perform a binding operation to add, remove or update the the target to the
   * parent. Examples include:
   * - Adding a user to a user group
   * - Adding a user to a system
   * - Updating a user bound to a system to set them to be a sudo user
   * @param parent The parent object, which has the following fields:
   * @param parent.type The parent object type, e.g. System.
   * @param parent.id The parent object id
   * @param operation The operation, e.g. 'add', 'remove', 'update'.
   * @param target The child, or target, object, which has the following fields:
   * @param target.type The target object type, e.g. User.
   * @param target.id The target object id.
   * @returns A promise that will be resolved if the binding operation was
   *  successful, or rejected if it wasn't.
   */
  bind(parent, operation, target) {
    return new Promise((resolve, reject) => {
      let endpoint = 'associations';

      // Extract fields used from each object so they can be swapped if needed
      // with out violating eslint's no-param-reassign rule.
      let parentType = parent.type;
      let parentId = parent.id;
      let targetType = target.type;
      let targetId = target.id;

      // If parent is group and target is homogenous, endpoint is /members. Here,
      // homogenous means that the target type and the parent type are the same,
      // e.g. parent.type = 'user_group' and target.type = 'user'.

      if (parentType.endsWith('_group')
          && (parentType === targetType || parentType.replace('_group', '') === targetType)) {
        endpoint = 'members';
      }

      // If parent is non-group and target is homogenous group then the endpoint
      // is /members, swap order. e.g. parent.type = ='user' and taget.type == 'user_group'.
      if (!parentType.endsWith('_group') && `${parentType}_group` === targetType) {
        endpoint = 'members';
        const tempType = parentType;
        const tempId = parentId;
        parentType = targetType;
        parentId = targetId;
        targetType = tempType;
        targetId = tempId;
      }

      const resource = this.getResourceFromParentType(parentType);
      targetType = this.convertTargetType(targetType);

      this
        .initialize()
        .then(() => {
          axios.post(`/api/v2/${resource}/${parentId}/${endpoint}`, {
            op: operation,
            type: targetType,
            id: targetId,
            attributes: target.attributes || null,
          })
            .then(() => {
              resolve();
            })
            .catch((error) => {
              let mesg = '';

              if (endpoint === 'members') {
                mesg = `Failed to add ${targetType} to group`;
              } else {
                mesg = `Failed to bind ${targetType} to ${parentType}`;
              }

              Api.handleError(reject, mesg, error);
            });
        })
        .catch((error) => {
          Api.handleError(reject, 'Unable to initialize DirectoryGroupingApi', error);
        });
    });
  }

  /**
   * This method allows the caller to manage the direct associations betweeen
   * two different objects, e.g. a system and a user.
   * @param payload An object with the following fields:
   * @param payload.parentType The parent object type, e.g. System.
   * @param payload.parentId The parent object id
   * @param payload.operation The operation, e.g. 'add', 'remove', 'update'.
   * @param payload.targetType The target, or child, object type, e.g. User.
   * @param payload.targetId The target object id.
   * @param payload.targetAttributes Optional attributes of the target to update
   * @see https://docs.jumpcloud.com/2.0/systems/manage-associations-of-a-system
   */
  bindAssociation({
    parentType, parentId, operation, targetType, targetId, targetAttributes = null,
  }) {
    return new Promise((resolve, reject) => {
      const parent = { type: parentType, id: parentId };
      const target = { type: targetType, id: targetId, attributes: targetAttributes };

      this
        .bind(parent, operation, target)
        .then(() => resolve())
        .catch(error => reject(error));
    });
  }

  /**
   * API v2 endpoints use "user" not "system_user" as the target type.
   */
  convertTargetType(targetType) {
    return targetType === ItemTypeConsts.systemUser ? 'user' : targetType;
  }

  /**
   * Get the target type objects that are directly associated, i.e. bound, to
   * the parent type object with the specified id.
   */
  getAssociations(parentType, parentId, targetType) {
    const params = {
      targets: [
        this.convertTargetType(targetType),
      ],
    };
    const resource = this.getResourceFromParentType(parentType);
    const url = `/api/v2/${resource}/${parentId}/associations?${this.param(params)}`;

    return this.getAllItems(url, { skip: 0, limit: 100 });
  }

  getGroupNames(groupType, groupIds) {
    return new Promise((resolve, reject) => {
      const tasks = [];
      const api = groupType === groupTypes.systemGroup ? SystemGroupApi : UserGroupApi;

      groupIds.forEach((groupId) => {
        tasks.push((cb) => {
          api
            .get(groupId)
            .then((data) => { cb(null, data); })
            .catch(error => cb(error));
        });
      });

      parallelLimit(tasks, 5, (error, results) => {
        if (error) {
          reject(error);
        } else {
          resolve(results);
        }
      });
    });
  }

  /**
   * Translate user_group to usergroup, system_group to system_group, etc. and
   * return the plural version of it.
   */
  getResourceFromParentType(parentType) {
    if (parentType === ItemTypeConsts.systemUser) {
      // API v2 endpoints use "users" not "systemusers"
      return 'users';
    }

    return pluralize(parentType.replace('_', ''));
  }
}

export default new DirectoryGroupingApiClass();
